import {
    collection,
    doc,
    addDoc,
    updateDoc,
    deleteDoc,
    getDocs,
    getDoc,
    query,
    where,
    orderBy,
    onSnapshot,
    Timestamp,
    Unsubscribe
} from 'firebase/firestore';
import { db } from '../firebase';

export interface ProductRef {
    id: string;
    title: string;
    handle?: string;
    imageUrl?: string;
}

export interface AutomationProgress {
    total: number;
    completed: number;
    failed: number;
    current?: string;
}

export interface AutomationJob {
    id: string;
    userId: string;
    shopId: string;
    shopName: string;
    promptId: string;
    promptTitle: string;
    promptContent: string;
    negativePrompt?: string;
    modelId: string;
    mode: 'auto-upload' | 'review';
    status: 'pending' | 'processing' | 'completed' | 'paused' | 'cancelled' | 'failed';
    products: ProductRef[];
    progress: AutomationProgress;
    estimatedTimeMs?: number;
    startedAt?: Date;
    completedAt?: Date;
    createdAt: Date;
    createdBy: string;
    error?: string;
}

export interface CreateAutomationJobData {
    userId: string;
    shopId: string;
    shopName: string;
    promptId: string;
    promptTitle: string;
    promptContent: string;
    negativePrompt?: string;
    modelId: string;
    mode: 'auto-upload' | 'review';
    products: ProductRef[];
    createdBy: string;
    useProductContext?: boolean;
}

// Create a new automation job
export async function createAutomationJob(data: CreateAutomationJobData): Promise<string> {
    const jobData = {
        ...data,
        status: 'pending',
        progress: {
            total: data.products.length,
            completed: 0,
            failed: 0,
        },
        estimatedTimeMs: data.products.length * 15000, // ~15 seconds per image
        createdAt: Timestamp.now(),
    };

    const docRef = await addDoc(collection(db, 'automationJobs'), jobData);
    return docRef.id;
}

// Get all jobs for a user
export async function getUserAutomationJobs(userId: string): Promise<AutomationJob[]> {
    const q = query(
        collection(db, 'automationJobs'),
        where('userId', '==', userId),
        orderBy('createdAt', 'desc')
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        startedAt: doc.data().startedAt?.toDate(),
        completedAt: doc.data().completedAt?.toDate(),
    } as AutomationJob));
}

// Get a single job by ID
export async function getAutomationJob(jobId: string): Promise<AutomationJob | null> {
    const docRef = doc(db, 'automationJobs', jobId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
        return null;
    }

    const data = docSnap.data();
    return {
        id: docSnap.id,
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        startedAt: data.startedAt?.toDate(),
        completedAt: data.completedAt?.toDate(),
    } as AutomationJob;
}

// Subscribe to real-time updates for a job
export function subscribeToJob(jobId: string, callback: (job: AutomationJob | null) => void): Unsubscribe {
    const docRef = doc(db, 'automationJobs', jobId);
    console.log('[subscribeToJob] Subscribing to job:', jobId);

    return onSnapshot(docRef, (docSnap) => {
        console.log('[subscribeToJob] Got snapshot for job:', jobId, 'exists:', docSnap.exists());
        if (!docSnap.exists()) {
            callback(null);
            return;
        }

        const data = docSnap.data();
        callback({
            id: docSnap.id,
            ...data,
            createdAt: data.createdAt?.toDate() || new Date(),
            startedAt: data.startedAt?.toDate(),
            completedAt: data.completedAt?.toDate(),
        } as AutomationJob);
    }, (error) => {
        console.error('[subscribeToJob] Firestore error for job', jobId, ':', error);
        // On error, call callback with null so the page doesn't hang
        callback(null);
    });
}


// Subscribe to real-time updates for all user jobs
export function subscribeToUserJobs(userId: string, callback: (jobs: AutomationJob[]) => void): Unsubscribe {
    const q = query(
        collection(db, 'automationJobs'),
        where('userId', '==', userId),
        orderBy('createdAt', 'desc')
    );

    return onSnapshot(q, (snapshot) => {
        const jobs = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            createdAt: doc.data().createdAt?.toDate() || new Date(),
            startedAt: doc.data().startedAt?.toDate(),
            completedAt: doc.data().completedAt?.toDate(),
        } as AutomationJob));
        callback(jobs);
    }, (error) => {
        console.error('[subscribeToUserJobs] Firestore error:', error);
        // Return empty array on error (index might not be ready)
        callback([]);
    });
}

// Cancel a job
export async function cancelAutomationJob(jobId: string): Promise<void> {
    const docRef = doc(db, 'automationJobs', jobId);
    await updateDoc(docRef, {
        status: 'cancelled',
        completedAt: Timestamp.now(),
    });
}

// Delete a job
export async function deleteAutomationJob(jobId: string): Promise<void> {
    const docRef = doc(db, 'automationJobs', jobId);
    await deleteDoc(docRef);
}

// Resume a paused job
export async function resumeAutomationJob(jobId: string): Promise<void> {
    const docRef = doc(db, 'automationJobs', jobId);
    await updateDoc(docRef, {
        status: 'pending', // Will be picked up by Cloud Function
    });
}

// Format estimated time for display
export function formatEstimatedTime(ms: number): string {
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);

    if (minutes > 0) {
        return `${minutes}min ${seconds}s`;
    }
    return `${seconds}s`;
}

// Calculate remaining time based on progress
export function calculateRemainingTime(job: AutomationJob): number {
    if (!job.estimatedTimeMs || job.progress.total === 0) {
        return 0;
    }

    const remaining = job.progress.total - job.progress.completed - job.progress.failed;
    const timePerItem = job.estimatedTimeMs / job.progress.total;
    return remaining * timePerItem;
}
